import React, { useState, useEffect } from 'react';

interface Invoice {
  id: number;
  invoiceNumber: string;
  familyName: string | null;
  isNonMember?: boolean;
  nonMemberName?: string;
  nonMemberEmail?: string;
  nonMemberCompany?: string;
  title: string;
  amount: number;
  paidAmount: number;
  status: string;
  type: string;
  issueDate: string;
  paidAt: string | null;
}

interface Donation {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  amount: number;
  status: string;
  donationDate: string;
  paymentMethod: string;
}

interface PeriodDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  period: string;
  year: number;
}

export default function PeriodDetailModal({ isOpen, onClose, period, year }: PeriodDetailModalProps) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [donations, setDonations] = useState<Donation[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'invoices' | 'donations'>('invoices');

  useEffect(() => {
    if (isOpen && period) {
      fetchPeriodData();
    }
  }, [isOpen, period, year]);

  const fetchPeriodData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`/api/admin/period-details?period=${encodeURIComponent(period)}&year=${year}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        setInvoices(result.data.invoices || []);
        setDonations(result.data.donations || []);
      }
    } catch (error) {
      console.error('Error fetching period details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const exportToCSV = () => {
    let csvContent = '';
    let filename = '';

    if (activeTab === 'invoices') {
      filename = `invoices_${period.replace(/\s+/g, '_')}.csv`;
      csvContent = 'Invoice Number,Family/Contact,Company,Title,Type,Amount,Paid Amount,Status,Issue Date,Paid Date\n';
      
      invoices.forEach(inv => {
        const contactName = inv.familyName || inv.nonMemberName || 'N/A';
        const company = inv.nonMemberCompany || '';
        csvContent += `"${inv.invoiceNumber}","${contactName}","${company}","${inv.title}","${inv.type}",${inv.amount},${inv.paidAmount},"${inv.status}","${formatDate(inv.issueDate)}","${formatDate(inv.paidAt)}"\n`;
      });
    } else {
      filename = `donations_${period.replace(/\s+/g, '_')}.csv`;
      csvContent = 'Name,Email,Amount,Status,Payment Method,Date\n';
      
      donations.forEach(don => {
        csvContent += `"${don.firstName} ${don.lastName}","${don.email}",${don.amount},"${don.status}","${don.paymentMethod}","${formatDate(don.donationDate)}"\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportAllToCSV = () => {
    const filename = `financial_report_${period.replace(/\s+/g, '_')}.csv`;
    let csvContent = 'Type,Reference,Name/Family,Amount,Status,Date\n';
    
    invoices.forEach(inv => {
      const contactName = inv.familyName || inv.nonMemberName || 'N/A';
      csvContent += `Invoice,"${inv.invoiceNumber}","${contactName}",${inv.paidAmount},"${inv.status}","${formatDate(inv.paidAt || inv.issueDate)}"\n`;
    });
    
    donations.forEach(don => {
      csvContent += `Donation,"-","${don.firstName} ${don.lastName}",${don.amount},"${don.status}","${formatDate(don.donationDate)}"\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  const totalInvoiceRevenue = invoices.reduce((sum, inv) => sum + parseFloat(inv.paidAmount.toString()), 0);
  const totalDonationRevenue = donations.reduce((sum, don) => sum + parseFloat(don.amount.toString()), 0);
  const totalRevenue = totalInvoiceRevenue + totalDonationRevenue;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-6xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-2xl shadow-xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-ggaa-green to-ggaa-primary px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-white">
                  Financial Details - {period}
                </h3>
                <p className="text-sm text-white/80 mt-1">
                  Total Revenue: {formatCurrency(totalRevenue)} ({invoices.length} invoices, {donations.length} donations)
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tabs and Export */}
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex space-x-1">
                <button
                  onClick={() => setActiveTab('invoices')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'invoices'
                      ? 'bg-white text-ggaa-green shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  📄 Invoices ({invoices.length})
                </button>
                <button
                  onClick={() => setActiveTab('donations')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'donations'
                      ? 'bg-white text-ggaa-green shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  💝 Donations ({donations.length})
                </button>
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={exportToCSV}
                  className="px-4 py-2 text-sm font-medium text-ggaa-green border border-ggaa-green rounded-lg hover:bg-ggaa-green hover:text-white transition-colors"
                >
                  📥 Export {activeTab === 'invoices' ? 'Invoices' : 'Donations'}
                </button>
                <button
                  onClick={exportAllToCSV}
                  className="px-4 py-2 text-sm font-medium text-white bg-ggaa-green rounded-lg hover:bg-ggaa-green/90 transition-colors"
                >
                  📥 Export All
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ggaa-green"></div>
                <span className="ml-2 text-gray-600">Loading...</span>
              </div>
            ) : activeTab === 'invoices' ? (
              <div>
                {invoices.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No invoices found for this period</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Family/Contact</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {invoices.map(invoice => (
                          <tr key={invoice.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {invoice.invoiceNumber}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {invoice.familyName ? (
                                <div>{invoice.familyName}</div>
                              ) : (
                                <div>
                                  <div className="font-medium">🏢 {invoice.nonMemberName || 'Non-Member'}</div>
                                  {invoice.nonMemberCompany && (
                                    <div className="text-xs text-gray-500">{invoice.nonMemberCompany}</div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {invoice.title}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                {invoice.type}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(invoice.amount)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-green-600">
                              {formatCurrency(invoice.paidAmount)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                                invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {invoice.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(invoice.paidAt || invoice.issueDate)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan={5} className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                            Total Invoice Revenue:
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-green-600">
                            {formatCurrency(totalInvoiceRevenue)}
                          </td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {donations.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No donations found for this period</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Method</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {donations.map(donation => (
                          <tr key={donation.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {donation.firstName} {donation.lastName}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {donation.email}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-green-600">
                              {formatCurrency(donation.amount)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                {donation.paymentMethod}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                donation.status === 'completed' ? 'bg-green-100 text-green-800' :
                                donation.status === 'failed' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {donation.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(donation.donationDate)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan={2} className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                            Total Donation Revenue:
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-green-600">
                            {formatCurrency(totalDonationRevenue)}
                          </td>
                          <td colSpan={3}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 